import React, { useState } from 'react';
import { Link } from 'gatsby';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { NavButton } from './header/NavButton';
import { DropdownMenu } from './header/DropdownMenu';
import { DropdownItem } from './header/DropdownItem';
import { SectionNav } from './header/SectionNav';
import { ToggleLanguageButton } from './layout/ToggleLanguageButton';
import { useLocalizedPaths } from '../hooks/useLocalizedPaths';
import { Menu, X } from 'lucide-react';

const Header = ({ location }) => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLocalizedPaths();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const pathname = location?.pathname || '/';
  const isHomePage = pathname === '/' || pathname === '/pl/';

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setMobileMenuOpen(false);
  };

  const closeDropdown = () => setDropdownOpen(false);

  const homepageSections = [
    { id: 'features-section', labelKey: t('features') },
    { id: 'agri-section', labelKey: t('agriculture') },
    { id: 'govtech-section', labelKey: t('govtech') },
    { id: 'applications-section', labelKey: t('applications') },
    { id: 'trusted-by-section', labelKey: t('partners') }
  ];

  const dropdownItems = [
    { to: getLocalizedPath('/'), labelKey: t('home') },
    { to: getLocalizedPath('/about/'), labelKey: t('about') },
    { to: getLocalizedPath('/blog/'), labelKey: t('blog') },
    { to: getLocalizedPath('/careers/'), labelKey: t('careers') },
    { to: getLocalizedPath('/offer/'), labelKey: t('offer') },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 bg-slate-900 bg-opacity-90 backdrop-blur-md z-50 border-b border-slate-800">
      <div className="container mx-auto px-4 sm:px-6 py-4 max-w-7xl">
        <div className="flex items-center justify-between">
          
          {/* Mobile menu button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-white hover:bg-slate-800 rounded transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <div className="relative">
              <NavButton
                onClick={() => setDropdownOpen(!dropdownOpen)}
                variant="dropdown"
              >
                {t('pages')}
                <span className="ml-1">▼</span>
              </NavButton>
            
              <DropdownMenu isOpen={dropdownOpen} onClose={closeDropdown}>
                {dropdownItems.map(({ to, labelKey }) => (
                  <DropdownItem
                    key={to}
                    to={to}
                    onClick={closeDropdown}
                  >
                    {labelKey}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </div>
            
            <SectionNav
              sections={homepageSections}
              onSectionClick={scrollToSection}
              isVisible={isHomePage}
            />
          </nav>
          
          <div className="flex-grow" />
          
          {/* Right side buttons */}
          <div className="flex items-center gap-2 sm:gap-4">
            <ToggleLanguageButton />
            
            <Link 
              to={getLocalizedPath('/contact/')} 
              className="bg-blue-600 px-3 sm:px-4 py-2 rounded text-white hover:bg-blue-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
              {t('contact')}
            </Link>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 border-t border-slate-700 pt-4">
            <nav className="flex flex-col space-y-2">
              {dropdownItems.map(({ to, labelKey }) => (
                <Link
                  key={to}
                  to={to}
                  onClick={() => setMobileMenuOpen(false)}
                  className="px-4 py-2 text-white hover:bg-slate-800 rounded transition-colors"
                >
                  {labelKey}
                </Link>
              ))}
              
              {isHomePage && (
                <>
                  <div className="border-t border-slate-700 my-2" />
                  {homepageSections.map(({ id, labelKey }) => (
                    <button
                      key={id}
                      onClick={() => scrollToSection(id)}
                      className="px-4 py-2 text-white hover:bg-slate-800 rounded transition-colors text-left"
                    >
                      {labelKey}
                    </button>
                  ))}
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export { Header };